<template>
	<div>
		<!-- dialog -->
		<v-dialog v-model="dialog.show" max-width="250" :persistent="submitLoading">
			<v-form ref="form" @submit.prevent="submit" :disabled="submitLoading">
				<v-card>
					<v-card-title class="justify-center text-capitalize primary--text">
						{{$T('add-journal')}}
					</v-card-title>
					<v-card-text>
						<bee-date-input
							v-model="dialog.date"
							:label="$T('inputs.date')"
							dense
							hide-details
							changeable-with-arrows
							changeable-on-scroll
							:rules="rules.required"
						/>
					</v-card-text>
					<v-card-actions>
						<v-spacer/>
						<v-btn color="primary" type="submit" :loading="submitLoading">
							{{$T('add')}}
						</v-btn>
						<v-btn @click="dialog.show = false" :disabled="submitLoading">
							{{$T('close')}}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-form>
		</v-dialog>

		<!-- delete dialog -->
		<v-delete-dialog
			v-model="deleteDialog"
			module-type="materialJournals"
			:action-name="!entryId ? 'delete' : 'deleteEntry'"
			:ids="entryId || $route.params.id"
			@on-delete="!entryId ? fetchJournals(true) : fetchJournal()"
			@input="!$event ? entryId = null : null"
		>
			{{$T('are-you-sure-you-want-to-$', { message: '' })}}
			<span class="text-uppercase alert-color--text">{{$T('delete')}}</span>
			{{$T(!entryId ? 'the-journal' : 'the-entry')}}
		</v-delete-dialog>

		<v-row dense align="center" class="mb-0">
			<!-- journalNo -->
			<v-col md="2" sm="3" cols="6">
				<div class="d-flex align-center">
					<v-btn
						icon
						:disabled="
							!materialJournalsNos.length ||
							journalNo === 0 ||
							journalNo === materialJournalsNos[0] ||
							tableLoading
						"
						@click="changeCurrentJournal(-1)"
					>
						<v-icon>mdi-chevron-left</v-icon>
					</v-btn>
					<v-text-field
						v-model.number="journalNo"
						:label="$T('inputs.no')"
						dense
						outlined
						menu-picker
						hide-details
						@keydown.enter="changeCurrentJournal(journalNo, true)"
					/>
					<v-btn
						icon
						:disabled="
							!materialJournalsNos.length ||
							journalNo === materialJournalsNos[materialJournalsNos.length - 1] ||
							tableLoading
						"
						@click="changeCurrentJournal(1)"
					>
						<v-icon>mdi-chevron-right</v-icon>
					</v-btn>
				</div>
			</v-col>
			
			<!-- date -->
			<v-col md="2" sm="3" cols="6">
				<v-text-field
					v-model="journalData.date"
					:label="$T('inputs.date')"
					dense
					outlined
					readonly
					prepend-icon="$calendar"
					hide-details
				/>
			</v-col>
			<!-- add-journal -->
			<v-col
				v-if="
					permissions.inventory.isReadWrite && ![
						materialJournalType.receiving,
						materialJournalType.delivery
					].includes(currentMaterialJournalType)
				"
				lg="2"
				md="2"
				cols="auto"
				class="text-center"
			>
				<v-btn small color="primary" @click="dialog.show = true">
					<v-icon class="me-1">$add</v-icon>
					{{$T('add-journal')}}
				</v-btn>
			</v-col>
			<v-spacer/>
			
			<v-col v-if="$route.params.id" cols="auto" class="d-flex align-center">
				<template v-if="!tableLoading">
					<!-- journal actions -->
					<div v-if="!previewRelatedEntry">
						<template v-if="permissions.inventory.isReadWrite">
							<v-btn
								v-if="typeMaterialJournal !== materialJournalType.rawSorting && typeMaterialJournal !== materialJournalType.sortingResults"
								:color="journalData.isPosted ? 'warning-color white--text' : 'primary'"
								class="me-2"
								:loading="submitLoading"
								@click="changePostState"
							>
								{{journalData.isPosted ? $T('unpost') : $T('post')}}
							</v-btn>
							<v-btn
								v-if="
									!journalData.isPosted && 
									(
										typeMaterialJournal !== materialJournalType.rawSorting 
										&&
										typeMaterialJournal !== materialJournalType.sortingResults
									)
								"
								color="alert-color white--text"
								@click="deleteDialog = true;"
							>
								{{$T('delete')}}
							</v-btn>
						</template>
					</div>
					<!-- related preview -->
					<div v-else>
						<v-btn
							v-if="
								(
									previewRelatedEntry.route.name === 'receiving' &&
									!permissions.purchases.isNone
								) || (
									previewRelatedEntry.route.name === 'sales' &&
									!permissions.sales.isNone
								)
							"
							color="preview-color white--text"
							:to="previewRelatedEntry.route"
						>
							<v-icon class="me-1">mdi-open-in-new</v-icon>
							{{previewRelatedEntry.text}}
						</v-btn>
						<v-spacer/>
					</div>

					<div v-if="previewRelatedProduction && !previewRelatedEntry">
						<v-btn
							v-if="
								(
									previewRelatedProduction.route.name === 'production-orders' &&
									!permissions.managingProductionOrders.isNone
								) || (
									previewRelatedProduction.route.name === 'order-results' &&
									!permissions.managingOrderResults.isNone
								)
							"
							color="preview-color white--text"
							:to="previewRelatedProduction.route"
						>
							<v-icon class="me-1">mdi-open-in-new</v-icon>
							{{previewRelatedProduction.text}}
						</v-btn>
						<v-spacer/>
					</div>
			
				</template>
				
				<v-card
					flat
					rounded="sm"
					class="px-6 py-3 ms-4 white--text"
					:class="journalData.isPosted ? 'success' : 'deep-orange'"
				>
					{{journalData.isPosted ? $T('posted') : $T('unposted')}}
				</v-card>
			</v-col>
		</v-row>

		<!-- table -->
		<bee-handy-smart-table
			v-model="tableInputs"
			ref="table"
			:headers="headers"
			:items="entires"
			hide-default-footer
			zebra
			dense
			:editable-rows="!journalData.isPosted && permissions.inventory.isReadWrite"
			:show-add-row="!journalData.isPosted && permissions.inventory.isReadWrite"
			stay-in-input-fields
			hide-inputs-details
			inputs-class="mb-0"
			:items-per-page="-1"
			:loading="loading || tableLoading || loadingWarehouse"
			:disable-context-menu="journalData.isPosted || permissions.inventory.isReadonly"
			:context-menu-items="permissions.inventory.isReadWrite ? contextMenuItems : []"
			:readonly-input-fields="loading || submitLoading || tableLoading || loadingWarehouse"
			:data-table-props="{ disableSort: true }"
			@context-menu-edit="({ item, index }) => { entryId = item.id; isEdit = true; }"
			@context-menu-delete="({ item }) => { entryId = item.id; deleteDialog = true; }"
			@edit-cancelled="isEdit = false; entryId = null;"
			@keypress.enter="submitEntry"
			@reached-last-input="submitEntry"
			@keydown.tab="inputsNavigationHandler"
		>
			<!-- account -->
			<template v-slot:input.accountId="{ on, attr }">
				<v-autocomplete
					v-on="on"
					v-bind="attr"
					item-text="name"
					item-value="id"
					:items="customers"
				/>
			</template>

			<!-- owner -->
			<template v-slot:input.ownerId="{ on, attr }">
				<v-autocomplete
					v-on="on"
					v-bind="attr"
					item-text="name"
					item-value="id"
					:items="owners"
				/>
			</template>
			
			<!-- item -->
			<template v-slot:input.itemId="{ on, attr }">
				<v-autocomplete
					v-on="on"
					v-bind="attr"
					item-text="name"
					item-value="id"
					:items="
						items.filter(c => c.type === type.sub).filter(c =>
							[materialJournalType.rawSorting, materialJournalType.sortingResults]
								.includes(currentMaterialJournalType)
								? currentMaterialJournalType === materialJournalType.rawSorting
									? c.id === rawCardamomId
									: c.id !== rawCardamomId
								: true
						)
					"
				/>
			</template>
		</bee-handy-smart-table>
	</div>
</template>

<script>
import rules from '@/helpers/validation-rules';
import moment from 'moment';
import VDeleteDialog from '@/components/VDeleteDialog.vue';
import { mapGetters, mapState } from 'vuex';
import { materialJournalType, type } from '@/helpers/enums';

export default {
	name: 'MaterialJournals',
	components: { VDeleteDialog },

	data() {
		return {
			// helpers
			materialJournalType,
			rules,
			previewRelatedEntry: null,

			loading: false,
			submitLoading: false,
			tableLoading: false,
			loadingWarehouse: false,

			dialog: {
				show: false,
				date: moment().format('YYYY-MM-DD'),
			},

			deleteDialog: false,
			isEdit: false,
			editIndex: null,
			type,

			journalData: {
				date: moment().format('YYYY-MM-DD'),
				isPosted: true,
			},
			journalNo: null,
			
			contextMenuItems: [
				{
					title: this.$T('edit').toUpperCase(), 
					event: 'edit', 
					class: 'warning-color--text',
					icon: '$edit', 
					iconClass: 'me-2', 
					iconColor: 'warning-color'
				},
				{
					title: this.$T('delete').toUpperCase(), 
					event: 'delete', 
					class: 'alert-color--text',
					icon: '$delete', 
					iconClass: 'me-2', 
					iconColor: 'alert-color'
				},
			],

			entryId: null,
			tableInputs: {
				date: moment().format('YYYY-MM-DD')
			},
			entires: [],
			materialJournals: [],
			previewRelatedProduction: null,
			typeMaterialJournal: null
		}
	},

	watch: {
		$route: {
			handler: function ({ params, name: newName }, { name: oldName } = {}) {
				if (params.id) {
					if (this.materialJournals.length) this.fetchJournal();
					else this.fetchJournals();
				} else if (!params.id || newName !== oldName) {
					this.fetchJournals(true);
				}
			},
			immediate: true
		},

		'dialog.show'(val) {
			if (!val) {
				this.dialog.date = moment().format('YYYY-MM-DD');
			}
		},

		'journalData.isPosted'(val) {
			if (!val) {
				this.$nextTick(() => {
					this.resetTableInputs();
					this.$nextTick(() => this.$refs.table.resetInputsFocus());
				})
			}
		}
	},

	computed: {
		...mapState({
			permissions: state => state.auth.self.permissions,
			owners: state => state.configs.configsTrees.owners,
			customers: state => state.configs.configsTrees.customers,
			items: state => state.items.items,
			detachmentId: state => state.configs.accountsConfigs.detachmentId,
			rawCardamomId: state => state.configs.itemsConfigs.rawCardamomId,
		}),
		...mapGetters({
			getAccountById: 'accounts/getAccountById',
			getItemById: 'items/getItemById',
			getMaterialJournalById: 'materialJournals/getMaterialJournalById',
			getMaterialJournalByNo: 'materialJournals/getMaterialJournalByNo',
		}),

		headers() {
			const inHeader = {
				text: this.$T('headers.in'),
				name: 'in',
				value: 'in',
				align: 'center',
				width: 80,
				type: 'number-format',
				inputRules: [(val) => {
					const outValue =
						+this.$options.filters.removeNumberFormat((this.tableInputs.out || '0') + '');
					const numVal =
						+this.$options.filters.removeNumberFormat((val || '0') + '');
					return (outValue > 0 && !numVal) || (numVal > 0 && !outValue);
				}]
			};
			const outHeader = {
				text: this.$T('headers.out'),
				name: 'out',
				value: 'out',
				align: 'center',
				width: 80,
				type: 'number-format',
				inputRules: [(val) => {
					const inValue =
						+this.$options.filters.removeNumberFormat((this.tableInputs.in || '0') + '');
					const numVal =
						+this.$options.filters.removeNumberFormat((val || '0') + '');
					return (inValue > 0 && !numVal) || (numVal > 0 && !inValue);
				}]
			};
			const ownerHeader = {
				text: this.$T('headers.owner'),
				name: 'ownerId',
				value: 'ownerId',
				align: 'center',
				width: 180,
				noNotAvailableText: true,
				setValueExpr: val => val ? this.getAccountById(val)?.name ?? '-' : 'Ugarit'
			};
			const accountHeader = {
				text: this.$T('headers.client'),
				name: 'accountId',
				value: 'accountId',
				align: 'center',
				width: 180,
				noNotAvailableText: true,
				setValueExpr: val => this.getAccountById(val)?.name ?? '-'
			};

			const headers = [
				{
					text: this.$T('headers.lot'),
					name: 'lot',
					value: 'lot',
					align: 'center',
				},
				{
					text: this.$T('headers.item'),
					name: 'itemId',
					value: 'itemId',
					align: 'center',
					width: 180,
					inputRules: rules.required,
					noNotAvailableText: true,
					setValueExpr: val => this.getItemById(val)?.name ?? '-'
				},
				{
					text: this.$T('headers.date'),
					name: 'date',
					value: 'date',
					align: 'center',
					type: 'date',
					width: 170,
					inputRules: rules.required
				},
				{
					text: this.$T('headers.note'),
					name: 'notes',
					value: 'notes',
					align: 'center',
				},
				{
					text: '',
					name: 'hint',
					value: 'hint',
					sortable: false,
					noInput: true,
					noNotAvailableText: true,
					columnClass: 'px-0'
				},
			];

			if (this.currentMaterialJournalType === materialJournalType.receiving) {
				headers.unshift(...[inHeader, ownerHeader])
			} else if (this.currentMaterialJournalType === materialJournalType.rawSorting) {
				headers.unshift(...[outHeader, ownerHeader])
			} else if (this.currentMaterialJournalType === materialJournalType.sortingResults) {
				headers.unshift(...[inHeader, ownerHeader])
			} else if (this.currentMaterialJournalType === materialJournalType.delivery) {
				headers.unshift(...[outHeader, accountHeader])
			} else {
				headers.unshift(...[inHeader, outHeader, ownerHeader, accountHeader])
			}

			for (let i = 0; i < headers.length; i++) {
				headers[i].columnClass = headers[i].columnClass ? ' px-1' : 'px-1';
			}

			return headers;
		},

		currentMaterialJournalType() {
			let journalType;
			switch (this.$route.name) {
			case 'receiving-journals': journalType = materialJournalType.receiving; break;
			case 'raw-cardamom-sorting-journals': journalType = materialJournalType.rawSorting; break;
			case 'cardamom-sorting-journals': journalType = materialJournalType.sortingResults; break;
			case 'delivery-journals': journalType = materialJournalType.delivery; break;
			}
			return journalType;
		},
		
		materialJournalsNos() {
			return this.materialJournals.filter(c =>
				this.currentMaterialJournalType ? c.type === this.currentMaterialJournalType : true
			).map(c => this.currentMaterialJournalType ? c.journalSubNo : c.journalNo).reverse();
		},
	},

	methods: {
		// helpers
		inputsNavigationHandler (e, headerName) {
			if (e.shiftKey) return;

			if (headerName === 'in' && this.tableInputs.in) {
				e.preventDefault();
				this.$refs.table.focus('ownerId');
			} else if (headerName === 'ownerId' && this.tableInputs.ownerId) {
				e.preventDefault();
				this.$refs.table.focus('lot');
			}
			
			this.$refs.table.validate();
		},

		resetTableInputs() {
			this.$refs.table.resetInputs({
				date: moment().format('YYYY-MM-DD'),
				itemId:
					this.currentMaterialJournalType === materialJournalType.rawSorting
						? this.rawCardamomId
						: null
			});
		},

		// fetching
		fetchJournal() {
			const journal = this.getMaterialJournalById(this.$route.params.id);
			if (!journal) return;
			this.entires = [];
			this.previewRelatedEntry = null;
			this.previewRelatedProduction = null;
			this.typeMaterialJournal = null;

			this.journalNo = this.currentMaterialJournalType ? journal.journalSubNo : journal.journalNo;
			this.journalData.date = moment(journal.date).format('YYYY-MM-DD');
			this.journalData.isPosted = journal.isPosted;
			this.tableLoading = true;
			this.$store.dispatch('materialJournals/fetchEntires', { journalId: journal.id }).then((data) => {
				this.typeMaterialJournal = journal.type;
				data.forEach(c => {
					this.entires.push({
						id: c.id,
						in: c.in,
						out: c.out,
						ownerId: c.ownerId,
						accountId: c.accountId,
						lot: c.lot,
						itemId: c.itemId,
						date: c.date && moment(c.date).format('YYYY-MM-DD'),
						notes: c.notes,
					})
					if (c.linkedEntry && !this.previewRelatedEntry) {
						let text, route;
						if (c.in >= 0 && c.out === null) {
							text = this.$T('preview-receiving');
							route = { name: 'receiving', query: { receivingId: c.linkedEntry } };
						} else if (c.in === null && c.out >= 0) {
							text = this.$T('preview-delivery');
							route = { name: 'sales', query: { deliveryId: c.linkedEntry } };
						}
						this.previewRelatedEntry = { text, route }
					} else if (c.type && !this.previewRelatedProduction) {
						let text, route;
						if (c.type === materialJournalType.rawSorting) {
							text = this.$T('preview-production-order')
							route = { name: 'production-orders', query: { id: journal.id } }
						} else if (c.type === materialJournalType.sortingResults) {
							text = this.$T('preview-order-results')
							route = { name: 'order-results', query: { id: journal.id } }
						}
						this.previewRelatedProduction = { text, route }
					}
				})
			}).finally(() => {
				this.tableLoading = false;
			})
		},

		fetchJournals(useLatest = false) {
			this.journalNo = null;
			this.entires = [];

			this.loading = true;
			this.$store.dispatch('materialJournals/fetchAll', {
				type: this.currentMaterialJournalType,
				isUpdate: true
			}).then((data) => {
				this.materialJournals = data;

				if (useLatest || !this.$route.params.id) {
					this.journalNo = this.currentMaterialJournalType 
						? data[0]?.journalSubNo 
						: data[0]?.journalNo;
				} else if (this.$route.params.id) {
					this.journalNo = this.currentMaterialJournalType 
						? this.getMaterialJournalById(this.$route.params.id)?.journalSubNo 
						: this.getMaterialJournalById(this.$route.params.id)?.journalNo;
				}
				this.changeCurrentJournal(this.journalNo, true);
				
				if (!useLatest) this.fetchJournal();
			}).finally(() => {
				this.loading = false;
			})
		},

		changeCurrentJournal(increment, isDirectChange = false) {
			if (!increment || !this.materialJournals.length) return;
			// [ 1, 2, 3, 1, 2, 2, 3, 1, 4, 3, 2, 3, 4, 5, 5, 6, 6, 4, 7 ]
			if (!isDirectChange) {
				const currentNoIndex = this.materialJournalsNos.indexOf(this.journalNo);
				console.log('currentNoIndex', currentNoIndex)
				if (currentNoIndex === -1) return;
				else if (
					(increment < 0 && currentNoIndex + increment >= 0) ||
					(increment > 0 && currentNoIndex + increment <= this.materialJournalsNos.length - 1)
				) {
					this.journalNo = this.materialJournalsNos[currentNoIndex + increment];
				}
			}
			const journal = this.materialJournals.find(c => this.currentMaterialJournalType 
				? c.journalSubNo === this.journalNo 
				: c.journalNo === this.journalNo);
			
			if (journal && journal.id !== this.$route.params.id) {
				this.$router.replace({ name: this.$route.name, params: { id: journal.id } });
			}
		},

		// submit
		submit() {
			if (this.$refs.form.validate()) {
				this.submitLoading = true;
				this.$store.dispatch('materialJournals/create', {
					date: this.dialog.date,
					materialJournalType:
						[materialJournalType.rawSorting, materialJournalType.sortingResults]
							.includes(this.currentMaterialJournalType)
							? this.currentMaterialJournalType
							: null
				}).then(() => {
					this.dialog.show = false;
					this.fetchJournals(true);
				}).finally(() => {
					this.submitLoading = false;
				})
			}
		},

		submitEntry() {
			if (this.$refs.table.validate()) {
				this.tableLoading = true;

				const accountId =
					[materialJournalType.rawSorting, materialJournalType.sortingResults]
						.includes(this.currentMaterialJournalType)
						? this.detachmentId
						: this.tableInputs.accountId;

				const actionName = !this.isEdit ? 'addEntry' : 'editEntry';
				this.$store.dispatch(`materialJournals/${actionName}`, {
					id: this.entryId,
					journalId: this.$route.params.id,
					inAmount: this.tableInputs.in || null,
					outAmount: this.tableInputs.out || null,
					accountId: accountId,
					ownerId: this.tableInputs.ownerId,
					lot: this.tableInputs.lot || 0,
					itemId: this.tableInputs.itemId,
					date: this.tableInputs.date,
					notes: this.tableInputs.notes || ''
				}).then(() => {
					this.isEdit = false;
					this.entryId = null;
					this.fetchJournal();
					this.$refs.table.resetEdit();
					this.resetTableInputs();
					this.$nextTick(() => {
						this.$nextTick(() => { this.$refs.table.resetInputsFocus(); })
					})
				}).catch(() => {
					this.tableLoading = false;
				})
			}
		},

		changePostState() {
			this.submitLoading = true;
			const actionName = !this.journalData.isPosted ? 'post' : 'unpost';
			this.$store.dispatch(`materialJournals/${actionName}`, {
				id: this.$route.params.id
			}).then(() => {
				this.fetchJournals();
			}).finally(() => {
				this.submitLoading = false;
			})
		},
	},

	created() {
		this.loadingWarehouse = true;
		this.$store.dispatch('warehouses/fetchDefaultWarhorse').finally(() => {
			this.loadingWarehouse = false;
		});
	},

	beforeRouteUpdate(to, from, next) {
		if (from) this.resetTableInputs();
		next();
	}

	/* beforeRouteEnter(to, from, next) {
		next(vm => {
			if (vm.permissions.inventory.isNone) {
				next({ name: 'home', replace: true });
			}
			return true;
		});
	} */
}
</script>

<style>

</style>